/**
 * Why a service's build failed, and what to do next, from the runner's state (`buildError`, `crashOutput`), for the
 * line under a "Build failed" badge and for an empty log of a failed build: a failure always says why, never
 * "No output yet".
 */

// The runner's wording when the sandbox has no Docker (container-live/preview-runner driver-trait NO_DOCKER_APP and
// compose.rs), current and older.
const NO_DOCKER = /docker isn't available on your plan|docker is not on in this sandbox|docker in the sandbox/i;

export const NO_DOCKER_REASON = "Docker isn't available on your plan or sandbox type";
export const NO_DOCKER_NEXT = 'Switch to a sandbox type with Docker.';
/** Where people pick their sandbox type (Settings › Sandbox and Backups › Sandbox type, components/sandbox/SandboxTypePicker). */
export const SANDBOX_TYPE_LINK = '/settings#set-sandbox-type';
export const NO_DOCKER_TYPE_NEXT = 'Your plan has no sandbox type with Docker: ask an admin.';

/**
 * Whether the person's plan offers a sandbox type with Docker, from the picker's view (`getMySandboxType`: `types[]`
 * with `docker` and `allowed`): true, false, or null when it is not known (no view, an older vfs-service).
 */
export function dockerTypeOffered(view) {
  if (!view || !Array.isArray(view.types)) { return null; }
  return view.types.some((type) => type && type.docker && type.allowed !== false);
}

const firstLine = (text) => String(text || '').split('\n').map((line) => line.trim()).find(Boolean) || '';

/**
 * `{ reason, next, link? }` for a failed build (`next` the step to take, `link` where to take it), or null when `service`
 * did not fail. Docker off: `{ docker: true }`, and `next` links to the sandbox-type picker unless `dockerType` (see
 * dockerTypeOffered) is false, when the plan has no type with Docker and only an admin can help. `reason` is the
 * runner's first line without its "Build failed: " / "failed to start dev server: " prefixes, else the last line the
 * process printed, else a plain "it did not say why".
 */
export function failureNote(service, { dockerType = null } = {}) {
  if (!service || !['failed', 'error'].includes(service.buildStatus)) { return null; }
  const error = firstLine(service.buildError);
  if (NO_DOCKER.test(error)) {
    return dockerType === false
      ? { reason: NO_DOCKER_REASON, next: NO_DOCKER_TYPE_NEXT, docker: true }
      : { reason: NO_DOCKER_REASON, next: NO_DOCKER_NEXT, link: SANDBOX_TYPE_LINK, docker: true };
  }
  const crash = (Array.isArray(service.crashOutput) ? service.crashOutput : []).map((line) => String(line || '').trim()).filter(Boolean);
  const reason = error.replace(/^Build failed:\s*/i, '').replace(/^failed to start dev server:\s*/i, '').trim() || crash[crash.length - 1] || '';
  if (!reason) { return { reason: 'The runner did not say why', next: 'Start it again to see its build output.' }; }
  return { reason, next: 'Fix it, then start the service again.' };
}

/** The note as one sentence: "Build failed: <reason>. <next>". */
export function failureText(note) {
  if (!note) { return ''; }
  const reason = note.reason.replace(/[.\s]+$/, '');
  return `Build failed: ${reason}. ${note.next}`;
}
